import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Storage } from '@ionic/storage-angular';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

import { reportStorageWarning } from 'src/app/common/storage/storage-telemetry';

/**
 * Diagnostics only, no functional impact (#26615).
 *
 * `currentUser` lives in Ionic Storage, which on device means IndexedDB inside the WebView, while
 * the OAuth tokens live in native secure storage. Those have very different lifetimes, so
 * "token present but currentUser missing" cannot tell us *why* the account is gone.
 *
 * To find out, the same generation id is written into three layers that fail independently:
 *
 * | layer          | store                             | dies when                                     |
 * |----------------|-----------------------------------|-----------------------------------------------|
 * | `indexedDb`    | Ionic Storage — holds currentUser | IndexedDB misbehaves, or WebKit purges        |
 * | `localStorage` | window.localStorage               | WebKit purges (same bucket, other engine)     |
 * | `native`       | secure storage (device only)      | app uninstalled, keychain reset               |
 *
 * Comparing them on startup separates the two failure classes that look identical today:
 *
 * - IndexedDB gone, localStorage alive → **IndexedDB specific** (`indexeddb-lost`). Changing the
 *   storage driver would help.
 * - both web layers gone, native alive → **web storage purged** (`web-storage-lost`). Changing the
 *   driver would change nothing; only a native store survives that.
 *
 * Everything here is best effort: it never throws, never blocks startup, never touches
 * `currentUser` and never influences the authentication flow.
 */
export type StorageGenerationVerdict =
  /** No marker anywhere — this installation has never run before. Harmless. */
  | 'fresh-install'
  /** All layers agree. */
  | 'intact'
  /** The store holding currentUser lost its data while localStorage kept it. */
  | 'indexeddb-lost'
  /** Both web layers lost their data while the native one kept it. */
  | 'web-storage-lost'
  /** Browser only: both web layers are empty and there is no native reference to compare with. */
  | 'web-storage-lost-or-fresh'
  /** Rare inverse of `indexeddb-lost`: localStorage lost its data, IndexedDB kept it. */
  | 'localstorage-lost'
  /** Device only: the native store lost its data while the web layers kept theirs. */
  | 'durable-storage-lost'
  /** Layers hold different ids — should not happen, worth knowing about. */
  | 'generation-mismatch'
  /** The comparison could not be completed. */
  | 'unknown';

export interface IStorageGenerationMarkers {
  indexedDb: string | null;
  localStorage: string | null;
  /** Always null in the browser: that layer does not exist there. */
  native: string | null;
}

export interface IStorageDiagnosticsSnapshot {
  verdict: StorageGenerationVerdict;
  /** The localforage driver Ionic Storage actually selected, e.g. `asyncStorage`. */
  driver: string | null;
  /** Which layers held a marker. Null until the comparison ran. */
  present: { indexedDb: boolean; localStorage: boolean; native: boolean } | null;
}

/**
 * Module level snapshot so consumers can enrich their own reports without taking a dependency on
 * this service (AuthService is constructed manually in its specs — adding a constructor parameter
 * there would touch a dozen call sites for a diagnostic).
 */
export const storageDiagnostics: IStorageDiagnosticsSnapshot = {
  verdict: 'unknown',
  driver: null,
  present: null,
};

/** Key inside Ionic Storage — the same store that holds `currentUser`. */
export const FRAGILE_GENERATION_KEY = 'storageGenerationId';
/** Key inside localStorage and inside the native secure storage. */
export const DURABLE_GENERATION_KEY = 'alberta_storage_generation';

/** Cap for a single store access; the native bridge can stall. */
const OPERATION_TIMEOUT_IN_MS = 800;
/** Overall cap, so a pathological environment cannot keep this running forever. */
const INITIALIZE_TIMEOUT_IN_MS = 2000;

const TIMED_OUT = Symbol('storage-diagnostics-timed-out');

/**
 * Pure classification of the three markers, kept separate so every combination can be covered by
 * tests without touching a store.
 *
 * @param hasNativeLayer false in the browser, where `native` is structurally absent, not lost
 */
export function classifyGenerations(
  markers: IStorageGenerationMarkers,
  hasNativeLayer: boolean
): StorageGenerationVerdict {
  const hasIndexedDb = markers.indexedDb != null;
  const hasLocalStorage = markers.localStorage != null;
  const hasNative = hasNativeLayer && markers.native != null;

  if (!hasIndexedDb && !hasLocalStorage && !hasNative) {
    // Nothing anywhere. On device that is unambiguous; in the browser a cleared storage looks
    // exactly like a browser that has never seen this app.
    return hasNativeLayer ? 'fresh-install' : 'web-storage-lost-or-fresh';
  }

  if (!hasIndexedDb) {
    // The store that matters is empty — the only open question is how far the loss reaches.
    if (hasLocalStorage) {
      return 'indexeddb-lost';
    }
    return hasNative ? 'web-storage-lost' : 'web-storage-lost-or-fresh';
  }

  if (!hasLocalStorage) {
    return 'localstorage-lost';
  }

  if (hasNativeLayer && !hasNative) {
    return 'durable-storage-lost';
  }

  const values = [markers.indexedDb, markers.localStorage, hasNativeLayer ? markers.native : null].filter(
    (value): value is string => value != null
  );
  return values.every(value => value === values[0]) ? 'intact' : 'generation-mismatch';
}

@Injectable({ providedIn: 'root' })
export class StorageDiagnosticsService {
  private _ready: Promise<void> | null = null;

  constructor(private _storage: Storage) {}

  /**
   * Compares the generation markers and realigns whichever layer is missing.
   *
   * Never throws and never runs longer than {@link INITIALIZE_TIMEOUT_IN_MS}. Callers are not
   * expected to await it: the app initializer starts it and moves on, so a stalling store cannot
   * delay startup by a single millisecond. In practice the comparison is long finished before the
   * first sync pipeline run needs the verdict. Repeated calls join the first run.
   */
  public initialize(): Promise<void> {
    if (this._ready != null) {
      return this._ready;
    }

    let timeoutHandle: ReturnType<typeof setTimeout>;
    const timeout = new Promise<void>(resolve => {
      timeoutHandle = setTimeout(resolve, INITIALIZE_TIMEOUT_IN_MS);
    });

    this._ready = Promise.race([this.compareGenerations(), timeout])
      .catch(() => undefined)
      .then(() => clearTimeout(timeoutHandle));

    return this._ready;
  }

  /** Compact one-liner for breadcrumbs. */
  public static describe(): string {
    const present = storageDiagnostics.present;
    const layers = present
      ? ` layers=idb:${present.indexedDb ? 1 : 0},ls:${present.localStorage ? 1 : 0},native:${present.native ? 1 : 0}`
      : '';
    return `verdict=${storageDiagnostics.verdict} driver=${storageDiagnostics.driver}${layers}`;
  }

  private async compareGenerations(): Promise<void> {
    storageDiagnostics.driver = this.readDriver();

    const hasNativeLayer = this.isNativePlatform();
    const markers: IStorageGenerationMarkers = {
      indexedDb: await this.readIndexedDb(),
      localStorage: this.readLocalStorage(),
      native: hasNativeLayer ? await this.readNative() : null,
    };

    storageDiagnostics.present = {
      indexedDb: markers.indexedDb != null,
      localStorage: markers.localStorage != null,
      native: markers.native != null,
    };
    storageDiagnostics.verdict = classifyGenerations(markers, hasNativeLayer);

    await this.realign(markers, hasNativeLayer);
    this.report();
  }

  /**
   * Brings every layer back to one id, so the same incident is not reported on every start. The
   * most durable surviving value wins; if nothing survived, a new generation begins.
   */
  private async realign(markers: IStorageGenerationMarkers, hasNativeLayer: boolean): Promise<void> {
    const reference = markers.native ?? markers.localStorage ?? markers.indexedDb ?? this.createGenerationId();

    const writes: Promise<void>[] = [];
    if (markers.indexedDb !== reference) {
      writes.push(this.writeIndexedDb(reference));
    }
    if (markers.localStorage !== reference) {
      writes.push(this.writeLocalStorage(reference));
    }
    if (hasNativeLayer && markers.native !== reference) {
      writes.push(this.writeNative(reference));
    }

    await Promise.all(writes);
  }

  private isNativePlatform(): boolean {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  }

  private readDriver(): string | null {
    try {
      return this._storage.driver;
    } catch {
      return null;
    }
  }

  private createGenerationId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private async withTimeout<T>(operation: () => Promise<T>): Promise<T | null> {
    let timeoutHandle: ReturnType<typeof setTimeout>;
    const timeout = new Promise<typeof TIMED_OUT>(resolve => {
      timeoutHandle = setTimeout(() => resolve(TIMED_OUT), OPERATION_TIMEOUT_IN_MS);
    });
    try {
      const result = await Promise.race([operation(), timeout]);
      return result === TIMED_OUT ? null : (result as T);
    } catch {
      return null;
    } finally {
      clearTimeout(timeoutHandle);
    }
  }

  private async readIndexedDb(): Promise<string | null> {
    return (await this.withTimeout(() => this._storage.get(FRAGILE_GENERATION_KEY))) ?? null;
  }

  private async writeIndexedDb(generationId: string): Promise<void> {
    // A failing write is itself part of the picture and shows up as a loss on the next start.
    await this.withTimeout(() => this._storage.set(FRAGILE_GENERATION_KEY, generationId));
  }

  private readLocalStorage(): string | null {
    try {
      return window.localStorage.getItem(DURABLE_GENERATION_KEY);
    } catch {
      // Touching localStorage can throw outright in restrictive privacy modes.
      return null;
    }
  }

  private async writeLocalStorage(generationId: string): Promise<void> {
    try {
      window.localStorage.setItem(DURABLE_GENERATION_KEY, generationId);
    } catch {
      // see readLocalStorage — a quota of 0 in private mode makes every write throw
    }
  }

  private async readNative(): Promise<string | null> {
    // The plugin rejects instead of returning null when the key does not exist; withTimeout
    // turns that into null.
    const stored = await this.withTimeout(() => SecureStoragePlugin.get({ key: DURABLE_GENERATION_KEY }));
    return stored?.value ?? null;
  }

  private async writeNative(generationId: string): Promise<void> {
    await this.withTimeout(() => SecureStoragePlugin.set({ key: DURABLE_GENERATION_KEY, value: generationId }));
  }

  private report(): void {
    const extras = {
      verdict: storageDiagnostics.verdict,
      driver: storageDiagnostics.driver,
      ...storageDiagnostics.present,
      isNative: this.isNativePlatform(),
    };

    try {
      window.logger?.addBreadcrumb({
        category: 'storage',
        message: `[26615] storage generation | ${StorageDiagnosticsService.describe()}`,
        data: extras,
      });
    } catch {
      // telemetry must never break startup
    }

    // 'intact' and 'fresh-install' are the expected cases and would only add noise.
    if (storageDiagnostics.verdict === 'intact' || storageDiagnostics.verdict === 'fresh-install') {
      return;
    }

    reportStorageWarning(
      `[26615] storage generation ${storageDiagnostics.verdict}`,
      new Error(`[26615] storage generation ${storageDiagnostics.verdict}`),
      extras
    );
  }
}
